import { toJalaliString } from "@kitchen/schemas";
import { Button, Card, Chip, faDigits, formatDateShort, formatMoney, Seg } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  accountingApi,
  type Account,
  type AccountLevel,
  type AccountingDetailTurnoverReport,
  type AccountingJournalReport,
  type AccountingLedgerReport,
  type AccountingTrialBalanceReport,
} from "../../api.js";

type ReportKind = "journal" | "ledger" | "trial-balance" | "detail-turnover";

const REPORT_TITLES: Record<ReportKind, string> = {
  journal: "دفتر روزنامه رسمی",
  ledger: "دفتر کل و معین",
  "trial-balance": "تراز آزمایشی ۴ ستونی",
  "detail-turnover": "گردش حساب‌های تفصیلی (اشخاص و مراکز)",
};

const LEVEL_LABELS: Record<AccountLevel, string> = {
  group: "گروه",
  general: "کل",
  subsidiary: "معین",
  detail: "تفصیلی",
};

export function ReportsTab() {
  const [reportKind, setReportKind] = useState<ReportKind>("journal");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [trialLevel, setTrialLevel] = useState<string>("subsidiary");
  const [detailFilter, setDetailFilter] = useState<string>("");

  // Accounts list for selecting account in ledger report
  const accountsQuery = useQuery({
    queryKey: ["accounting", "accounts"],
    queryFn: () => accountingApi.listAccounts(),
  });
  const accounts: Account[] = accountsQuery.data ?? [];

  // Default selected account if empty
  const activeAccountId = useMemo(() => {
    if (selectedAccountId) return selectedAccountId;
    const firstSub = accounts.find((a) => a.level === "subsidiary" || a.level === "general");
    return firstSub?.id ?? "";
  }, [accounts, selectedAccountId]);

  // Query options
  const dateOptions = useMemo(() => {
    return {
      ...(fromDate.trim() ? { fromDate: fromDate.trim() } : {}),
      ...(toDate.trim() ? { toDate: toDate.trim() } : {}),
    };
  }, [fromDate, toDate]);

  // 1. Journal Query
  const journalQuery = useQuery<AccountingJournalReport>({
    queryKey: ["accounting", "reports", "journal", dateOptions],
    queryFn: () => accountingApi.getJournalReport(dateOptions),
    enabled: reportKind === "journal",
  });

  // 2. Ledger Query
  const ledgerQuery = useQuery<AccountingLedgerReport | null>({
    queryKey: ["accounting", "reports", "ledger", activeAccountId, dateOptions],
    queryFn: () => {
      if (!activeAccountId) return Promise.resolve(null);
      return accountingApi.getLedgerReport({
        accountId: activeAccountId,
        ...dateOptions,
      });
    },
    enabled: reportKind === "ledger" && Boolean(activeAccountId),
  });

  // 3. Trial Balance Query
  const trialBalanceQuery = useQuery<AccountingTrialBalanceReport>({
    queryKey: ["accounting", "reports", "trial-balance", trialLevel, dateOptions],
    queryFn: () => {
      const levelParam = trialLevel === "all" ? undefined : (trialLevel as AccountLevel);
      return accountingApi.getTrialBalanceReport({
        ...dateOptions,
        ...(levelParam ? { level: levelParam } : {}),
      });
    },
    enabled: reportKind === "trial-balance",
  });

  // 4. Detail Turnover Query
  const detailTurnoverQuery = useQuery<AccountingDetailTurnoverReport>({
    queryKey: ["accounting", "reports", "detail-turnover", detailFilter, dateOptions],
    queryFn: () => {
      const orgParam = detailFilter.trim() ? detailFilter.trim() : undefined;
      return accountingApi.getDetailTurnoverReport({
        ...dateOptions,
        ...(orgParam ? { organizationId: orgParam } : {}),
      });
    },
    enabled: reportKind === "detail-turnover",
  });

  const journalData = journalQuery.data;
  const ledgerData = ledgerQuery.data;
  const trialData = trialBalanceQuery.data;
  const detailData = detailTurnoverQuery.data;

  const printDateJalali = useMemo(() => {
    const today = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Tehran",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
    return toJalaliString(today);
  }, []);

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      <style>{`
        @media print {
          .no-print, header.top, nav.nav, aside.rail, .top-bar, .report-nav, .report-filters {
            display: none !important;
          }
          body, #root, .app-shell, .main {
            background: #fff !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .a4-sheet {
            box-shadow: none !important;
            border: none !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 5mm !important;
          }
        }
        .a4-sheet {
          background: #fff;
          color: #111;
          border-radius: var(--r-card);
          border: 1px solid var(--line);
          padding: var(--s6);
          box-shadow: var(--sh-card);
        }
        .a4-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 2px solid var(--line);
          padding-bottom: var(--s4);
          margin-bottom: var(--s5);
        }
        .report-table {
          width: 100%;
          border-collapse: collapse;
          font-size: var(--fs-14);
        }
        .report-table th, .report-table td {
          border: 1px solid var(--line);
          padding: var(--s2) var(--s3);
          text-align: right;
        }
        .report-table th {
          background: var(--canvas);
          font-weight: 600;
        }
        .report-table tfoot td {
          background: var(--canvas);
          font-weight: 700;
        }
      `}</style>

      {/* Nav and Controls (hidden on print) */}
      <div className="no-print report-nav row wrap justify-between items-center" style={{ gap: "var(--s3)" }}>
        <Seg<ReportKind>
          label="انتخاب گزارش مالی"
          value={reportKind}
          onChange={(val) => {
            setReportKind(val);
          }}
          options={[
            { value: "journal", label: "دفتر روزنامه" },
            { value: "ledger", label: "دفتر کل و معین" },
            { value: "trial-balance", label: "تراز آزمایشی" },
            { value: "detail-turnover", label: "گردش تفصیلی" },
          ]}
        />

        <Button
          variant="primary"
          onClick={() => {
            window.print();
          }}
        >
          چاپ گزارش رسمی (A4)
        </Button>
      </div>

      {/* Filter Toolbar (hidden on print) */}
      <Card className="no-print report-filters">
        <div className="row wrap items-center" style={{ gap: "var(--s4)" }}>
          <div className="row items-center" style={{ gap: "var(--s2)" }}>
            <span className="text-sm bold">از تاریخ:</span>
            <input
              type="text"
              className="input"
              placeholder="مثال: 2026-01-01"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
              }}
              style={{ width: "140px" }}
            />
          </div>

          <div className="row items-center" style={{ gap: "var(--s2)" }}>
            <span className="text-sm bold">تا تاریخ:</span>
            <input
              type="text"
              className="input"
              placeholder="مثال: 2026-12-29"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
              }}
              style={{ width: "140px" }}
            />
          </div>

          {reportKind === "ledger" && (
            <div className="row items-center" style={{ gap: "var(--s2)" }}>
              <span className="text-sm bold">حساب:</span>
              <select
                className="select"
                value={activeAccountId}
                onChange={(e) => {
                  setSelectedAccountId(e.target.value);
                }}
                style={{ minWidth: "240px" }}
              >
                <option value="">-- انتخاب حساب --</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} — {a.name} ({LEVEL_LABELS[a.level]})
                  </option>
                ))}
              </select>
            </div>
          )}

          {reportKind === "trial-balance" && (
            <div className="row items-center" style={{ gap: "var(--s2)" }}>
              <span className="text-sm bold">سطح سرفصل:</span>
              <select
                className="select"
                value={trialLevel}
                onChange={(e) => {
                  setTrialLevel(e.target.value);
                }}
                style={{ width: "160px" }}
              >
                <option value="subsidiary">سطح معین (استاندارد)</option>
                <option value="general">سطح کل</option>
                <option value="group">سطح گروه</option>
                <option value="all">همه سطوح</option>
              </select>
            </div>
          )}

          {reportKind === "detail-turnover" && (
            <div className="row items-center" style={{ gap: "var(--s2)" }}>
              <span className="text-sm bold">شناسه شخص/سازمان:</span>
              <input
                type="text"
                className="input"
                placeholder="فیلتر اختیاری شناسه..."
                value={detailFilter}
                onChange={(e) => {
                  setDetailFilter(e.target.value);
                }}
                style={{ minWidth: "200px" }}
              />
            </div>
          )}

          {(fromDate || toDate || detailFilter) && (
            <Button
              variant="ghost"
              onClick={() => {
                setFromDate("");
                setToDate("");
                setDetailFilter("");
              }}
            >
              پاک‌سازی فیلترها
            </Button>
          )}
        </div>
      </Card>

      {/* Printable Sheet View */}
      <div className="a4-sheet" dir="rtl">
        {/* Official Header */}
        <div className="a4-header">
          <div>
            <h2 style={{ margin: 0, fontSize: "var(--fs-20)" }}>سیستم حسابداری مالی و دوبل</h2>
            <div className="text-secondary text-sm" style={{ marginTop: "var(--s1)" }}>
              {REPORT_TITLES[reportKind]}
            </div>
          </div>
          <div className="text-sm" style={{ textAlign: "left" }}>
            <div>تاریخ چاپ: {printDateJalali}</div>
            <div className="text-secondary">
              بازه زمانی: {fromDate ? formatDateShort(fromDate) : "ابتدای دوره"} تا{" "}
              {toDate ? formatDateShort(toDate) : "انتهای دوره"}
            </div>
          </div>
        </div>

        {/* 1. JOURNAL REPORT */}
        {reportKind === "journal" && (
          <div className="stack" style={{ gap: "var(--s4)" }}>
            {journalQuery.isLoading ? (
              <div className="center text-secondary">در حال دریافت دفتر روزنامه...</div>
            ) : !journalData || journalData.entries.length === 0 ? (
              <div className="center text-secondary">هیچ تراکنش ثبتی در این بازه یافت نشد.</div>
            ) : (
              <div className="table-wrap">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th style={{ width: "80px" }}>شماره سند</th>
                      <th style={{ width: "90px" }}>تاریخ</th>
                      <th>کد و نام حساب</th>
                      <th>شرح آرتیکل / سند</th>
                      <th style={{ width: "120px", textAlign: "left" }}>بدهکار (ریال)</th>
                      <th style={{ width: "120px", textAlign: "left" }}>بستانکار (ریال)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {journalData.entries.flatMap((entry) =>
                      entry.lines.map((line, lineIdx) => (
                        <tr key={`${entry.id}-${line.accountId}-${String(lineIdx)}`}>
                          <td className="center bold">
                            {lineIdx === 0 ? faDigits(entry.voucherNumber) : ""}
                          </td>
                          <td>{lineIdx === 0 ? formatDateShort(entry.date) : ""}</td>
                          <td>
                            <span className="bold">{line.accountCode}</span> — {line.accountName}
                          </td>
                          <td>{line.description || entry.description}</td>
                          <td style={{ textAlign: "left" }}>
                            {line.debitRial > 0 ? formatMoney(line.debitRial) : "—"}
                          </td>
                          <td style={{ textAlign: "left" }}>
                            {line.creditRial > 0 ? formatMoney(line.creditRial) : "—"}
                          </td>
                        </tr>
                      )),
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={4} className="bold" style={{ textAlign: "left" }}>
                        جمع کل دفتر روزنامه ({faDigits(journalData.voucherCount)} سند):
                      </td>
                      <td className="bold" style={{ textAlign: "left" }}>
                        {formatMoney(journalData.totalDebitRial)}
                      </td>
                      <td className="bold" style={{ textAlign: "left" }}>
                        {formatMoney(journalData.totalCreditRial)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}

        {/* 2. LEDGER REPORT */}
        {reportKind === "ledger" && (
          <div className="stack" style={{ gap: "var(--s4)" }}>
            {!activeAccountId ? (
              <div className="center text-secondary">لطفاً ابتدا یک حساب را انتخاب کنید.</div>
            ) : ledgerQuery.isLoading ? (
              <div className="center text-secondary">در حال محاسبه و تجمیع دفتر کل/معین...</div>
            ) : !ledgerData ? (
              <div className="center text-secondary">اطلاعات حساب یافت نشد.</div>
            ) : (
              <div className="stack" style={{ gap: "var(--s3)" }}>
                {/* Account Details & Opening Balance Bar */}
                <div
                  className="row wrap justify-between items-center"
                  style={{
                    padding: "var(--s3)",
                    background: "var(--canvas)",
                    borderRadius: "var(--r-card)",
                    border: "1px solid var(--line)",
                  }}
                >
                  <div className="row items-center" style={{ gap: "var(--s3)" }}>
                    <span className="bold" style={{ fontSize: "var(--fs-16)" }}>
                      {ledgerData.account.code} — {ledgerData.account.name}
                    </span>
                    <Chip tone="blue">{LEVEL_LABELS[ledgerData.account.level]}</Chip>
                    <Chip tone="dark">
                      {ledgerData.account.nature === "debit" ? "طبیعت بدهکار" : "طبیعت بستانکار"}
                    </Chip>
                  </div>

                  <div className="row items-center" style={{ gap: "var(--s4)" }}>
                    <div className="text-sm">
                      <span className="text-secondary">مانده ابتدای دوره: </span>
                      <span className="bold">
                        {formatMoney(
                          Math.abs(ledgerData.openingBalanceDebitRial - ledgerData.openingBalanceCreditRial),
                        )}
                      </span>{" "}
                      <span>
                        (
                        {ledgerData.openingBalanceDebitRial >= ledgerData.openingBalanceCreditRial
                          ? "بد"
                          : "بس"}
                        )
                      </span>
                    </div>
                  </div>
                </div>

                {/* Ledger Transactions Table */}
                <div className="table-wrap">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th style={{ width: "80px" }}>شماره سند</th>
                        <th style={{ width: "90px" }}>تاریخ</th>
                        <th>شرح آرتیکل / سند</th>
                        <th style={{ width: "120px", textAlign: "left" }}>بدهکار</th>
                        <th style={{ width: "120px", textAlign: "left" }}>بستانکار</th>
                        <th style={{ width: "130px", textAlign: "left" }}>مانده</th>
                        <th style={{ width: "60px", textAlign: "center" }}>تشخیص</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Opening Balance Row */}
                      <tr style={{ background: "rgba(0,0,0,0.02)" }}>
                        <td className="center">—</td>
                        <td>{fromDate ? formatDateShort(fromDate) : "—"}</td>
                        <td className="bold">مانده ابتدای دوره / نقل از قبل</td>
                        <td style={{ textAlign: "left" }}>
                          {ledgerData.openingBalanceDebitRial > 0
                            ? formatMoney(ledgerData.openingBalanceDebitRial)
                            : "—"}
                        </td>
                        <td style={{ textAlign: "left" }}>
                          {ledgerData.openingBalanceCreditRial > 0
                            ? formatMoney(ledgerData.openingBalanceCreditRial)
                            : "—"}
                        </td>
                        <td className="bold" style={{ textAlign: "left" }}>
                          {formatMoney(
                            Math.abs(
                              ledgerData.openingBalanceDebitRial - ledgerData.openingBalanceCreditRial,
                            ),
                          )}
                        </td>
                        <td className="center bold">
                          {ledgerData.openingBalanceDebitRial >= ledgerData.openingBalanceCreditRial
                            ? "بد"
                            : "بس"}
                        </td>
                      </tr>

                      {/* Transaction Rows */}
                      {ledgerData.entries.map((entry, idx) => {
                        const isDebit = entry.balanceDebitRial >= entry.balanceCreditRial;
                        const bal = isDebit ? entry.balanceDebitRial : entry.balanceCreditRial;
                        return (
                          <tr key={`${entry.voucherId}-${String(idx)}`}>
                            <td className="center bold">{faDigits(entry.voucherNumber)}</td>
                            <td>{formatDateShort(entry.date)}</td>
                            <td>{entry.description}</td>
                            <td style={{ textAlign: "left" }}>
                              {entry.debitRial > 0 ? formatMoney(entry.debitRial) : "—"}
                            </td>
                            <td style={{ textAlign: "left" }}>
                              {entry.creditRial > 0 ? formatMoney(entry.creditRial) : "—"}
                            </td>
                            <td className="bold" style={{ textAlign: "left" }}>
                              {formatMoney(bal)}
                            </td>
                            <td className="center bold">{isDebit ? "بد" : "بس"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3} className="bold" style={{ textAlign: "left" }}>
                          جمع گردش طی دوره و مانده نهایی:
                        </td>
                        <td className="bold" style={{ textAlign: "left" }}>
                          {formatMoney(ledgerData.totalTurnDebitRial)}
                        </td>
                        <td className="bold" style={{ textAlign: "left" }}>
                          {formatMoney(ledgerData.totalTurnCreditRial)}
                        </td>
                        <td className="bold" style={{ textAlign: "left" }}>
                          {formatMoney(
                            ledgerData.closingBalanceDebitRial >= ledgerData.closingBalanceCreditRial
                              ? ledgerData.closingBalanceDebitRial
                              : ledgerData.closingBalanceCreditRial,
                          )}
                        </td>
                        <td className="center bold">
                          {ledgerData.closingBalanceDebitRial >= ledgerData.closingBalanceCreditRial
                            ? "بد"
                            : "بس"}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 3. TRIAL BALANCE REPORT */}
        {reportKind === "trial-balance" && (
          <div className="stack" style={{ gap: "var(--s4)" }}>
            {trialBalanceQuery.isLoading ? (
              <div className="center text-secondary">در حال محاسبه تراز آزمایشی...</div>
            ) : !trialData || trialData.rows.length === 0 ? (
              <div className="center text-secondary">هیچ رکوردی در تراز آزمایشی یافت نشد.</div>
            ) : (
              <div className="stack" style={{ gap: "var(--s3)" }}>
                <div className="row justify-between items-center">
                  <div className="row items-center" style={{ gap: "var(--s2)" }}>
                    <span className="bold">وضعیت توازن تراز آزمایشی:</span>
                    {trialData.isBalanced ? (
                      <Chip tone="ok">کاملاً تراز و متعادل</Chip>
                    ) : (
                      <Chip tone="coral">عدم توازن تراز</Chip>
                    )}
                  </div>
                </div>

                <div className="table-wrap">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th rowSpan={2} style={{ width: "90px" }}>
                          کد حساب
                        </th>
                        <th rowSpan={2}>نام حساب</th>
                        <th rowSpan={2} style={{ width: "70px" }}>
                          سطح
                        </th>
                        <th colSpan={2} className="center">
                          گردش دوره
                        </th>
                        <th colSpan={2} className="center">
                          مانده انتهای دوره
                        </th>
                      </tr>
                      <tr>
                        <th style={{ width: "120px", textAlign: "left" }}>بدهکار</th>
                        <th style={{ width: "120px", textAlign: "left" }}>بستانکار</th>
                        <th style={{ width: "120px", textAlign: "left" }}>بدهکار</th>
                        <th style={{ width: "120px", textAlign: "left" }}>بستانکار</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trialData.rows.map((r) => (
                        <tr key={r.account.id}>
                          <td className="bold">{r.account.code}</td>
                          <td>{r.account.name}</td>
                          <td>
                            <Chip tone="dark">{LEVEL_LABELS[r.account.level]}</Chip>
                          </td>
                          <td style={{ textAlign: "left" }}>
                            {r.turnDebitRial > 0 ? formatMoney(r.turnDebitRial) : "—"}
                          </td>
                          <td style={{ textAlign: "left" }}>
                            {r.turnCreditRial > 0 ? formatMoney(r.turnCreditRial) : "—"}
                          </td>
                          <td className="bold" style={{ textAlign: "left" }}>
                            {r.closingBalanceDebitRial > 0 ? formatMoney(r.closingBalanceDebitRial) : "—"}
                          </td>
                          <td className="bold" style={{ textAlign: "left" }}>
                            {r.closingBalanceCreditRial > 0 ? formatMoney(r.closingBalanceCreditRial) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3} className="bold" style={{ textAlign: "left" }}>
                          جمع ستون‌های تراز:
                        </td>
                        <td className="bold" style={{ textAlign: "left" }}>
                          {formatMoney(trialData.totalTurnDebitRial)}
                        </td>
                        <td className="bold" style={{ textAlign: "left" }}>
                          {formatMoney(trialData.totalTurnCreditRial)}
                        </td>
                        <td className="bold" style={{ textAlign: "left" }}>
                          {formatMoney(trialData.totalClosingDebitRial)}
                        </td>
                        <td className="bold" style={{ textAlign: "left" }}>
                          {formatMoney(trialData.totalClosingCreditRial)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 4. DETAIL TURNOVER REPORT */}
        {reportKind === "detail-turnover" && (
          <div className="stack" style={{ gap: "var(--s4)" }}>
            {detailTurnoverQuery.isLoading ? (
              <div className="center text-secondary">در حال محاسبه گردش تفصیلی...</div>
            ) : !detailData || detailData.rows.length === 0 ? (
              <div className="center text-secondary">هیچ تراکنش تفصیلی یافت نشد.</div>
            ) : (
              <div className="table-wrap">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th style={{ width: "80px" }}>ردیف</th>
                      <th>شخص / مرکز هزینه / سازمان</th>
                      <th style={{ width: "130px", textAlign: "left" }}>گردش بدهکار</th>
                      <th style={{ width: "130px", textAlign: "left" }}>گردش بستانکار</th>
                      <th style={{ width: "130px", textAlign: "left" }}>مانده نهایی</th>
                      <th style={{ width: "70px", textAlign: "center" }}>تشخیص</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailData.rows.map((r, i) => {
                      const isDebit = r.closingBalanceDebitRial >= r.closingBalanceCreditRial;
                      const bal = isDebit ? r.closingBalanceDebitRial : r.closingBalanceCreditRial;
                      return (
                        <tr key={r.organizationId || String(i)}>
                          <td className="center">{faDigits(i + 1)}</td>
                          <td className="bold">
                            {r.organizationName || r.organizationId || "سایر تفصیلی‌ها"}
                          </td>
                          <td style={{ textAlign: "left" }}>
                            {r.turnDebitRial > 0 ? formatMoney(r.turnDebitRial) : "—"}
                          </td>
                          <td style={{ textAlign: "left" }}>
                            {r.turnCreditRial > 0 ? formatMoney(r.turnCreditRial) : "—"}
                          </td>
                          <td className="bold" style={{ textAlign: "left" }}>
                            {formatMoney(bal)}
                          </td>
                          <td className="center bold">{isDebit ? "بد" : "بس"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2} className="bold" style={{ textAlign: "left" }}>
                        جمع کل گردش تفصیلی:
                      </td>
                      <td className="bold" style={{ textAlign: "left" }}>
                        {formatMoney(detailData.totalTurnDebitRial)}
                      </td>
                      <td className="bold" style={{ textAlign: "left" }}>
                        {formatMoney(detailData.totalTurnCreditRial)}
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
